import React, { useCallback, useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { useHistory, useParams } from "react-router-dom";
import { observer } from "mobx-react";
import { Step } from "src/screens/process/step/Step";
import { useForm, SubmitHandler } from "react-hook-form";
import { runInAction } from "mobx";
import { Route, selectRoute } from "src/config/routes";
import { useTranslation } from "react-i18next";
import { Notification } from "src/api/Notification";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const TextBase = () => {
    const { t } = useTranslation();
    const { easyContactStore, uiStore } = useContext(RootStoreContext);
    const history = useHistory();
    const { processId, stepId } = useParams<{ processId: string; stepId: string }>();
    const { nextStep } = easyContactStore.stepNavigation;
    const step = easyContactStore.getStepById(processId, stepId);
    const isTextFieldRequired = step?.parameters?.required;

    const text_schema = yup.object().shape({
        message: yup.string().required(t("form.required_field"))
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        setFocus
    } = useForm<Notification>({
        resolver: isTextFieldRequired ? yupResolver(text_schema) : undefined
    });

    useEffect(() => {
        const currentURL = window.location.href;

        // Find the index of '?' in the URL
        const questionMarkIndex = currentURL.indexOf("?");

        if (questionMarkIndex !== -1) {
            // Extract the query string from the URL
            const queryString = currentURL.substring(questionMarkIndex + 1);

            // Split the query string into key-value pairs
            const queryParams = queryString.split("&");

            // Iterate through the key-value pairs to find 'loginrequest'
            if (queryParams.length > 0 && queryParams[0] === "loginrequest=true") {
                easyContactStore.notification.message = t("screens.text.generated_login_request");
            }
        }
    }, [easyContactStore.notification, t]);

    useEffect(() => {
        setFocus("message");
        setValue("message", easyContactStore.notification.message);
    }, [easyContactStore.notification.message, setValue, setFocus]);

    const onSubmit: SubmitHandler<Notification> = useCallback(
        (data: Notification) => {
            runInAction(() => {
                if (data.message) {
                    easyContactStore.setMessage(data.message);
                }
            });

            if (nextStep) {
                history.push(
                    selectRoute(Route.step, { processId, stepType: String(nextStep.type), stepId: nextStep.id })
                );
            }
        },
        [history, easyContactStore, nextStep, processId]
    );

    return (
        <Step>
            <UI.Container className="pt-4">
                <UI.Row>
                    <UI.Col md={uiStore.defaultColumnSize.md}>
                        <UI.Form onSubmit={handleSubmit(onSubmit)}>
                            <UI.Input
                                id="message"
                                {...register("message")}
                                label={`${t("screens.text.message")}${isTextFieldRequired ? " *" : ""}`}
                                type="text"
                                as="textarea"
                                errorMsg={errors.message ? errors.message.message : undefined}
                            />
                            <UI.Button onClick={handleSubmit(onSubmit)} label={t("labels.continue")} />
                        </UI.Form>
                    </UI.Col>
                </UI.Row>
            </UI.Container>
        </Step>
    );
};

export const Text = Sentry.withProfiler(observer(TextBase));
